<template>
  <div>
    <!-- The one control that reopens a hidden views panel. `ViewsPanel` is
         `display: none` in this state (`views_panel.scss`), which takes its
         own collapse toggle with it -- the same reason `app.vue` keeps
         `layout__open-nav` outside `Sidebar`. Positioned clear of that
         control: both can be on screen together when the sidebar is also
         hidden, and a phone-width user still needs to tell them apart. -->
    <button
      v-if="showViewsPanel && viewsPanelHidden"
      type="button"
      class="layout__open-views"
      :aria-label="$t('viewsPanel.openViews')"
      @click="setViewsPanelCollapsed(false)"
    >
      <i class="baserow-icon-views-panel" aria-hidden="true"></i>
    </button>
    <!-- `05-sidebar-system.md` §3.3: floating over the grid makes this a modal
         surface, and `app.vue` already stops the sidebar's Overlay leaking
         clicks through to the content behind it via the same component. The
         rail's own `views_panel.scss` overlay CSS never had a scrim, so a
         click meant to browse views could land on a cell instead -- measured
         live, `elementFromPoint` behind the open panel returned a grid cell,
         not the panel or nothing. -->
    <PanelOverlayScrim
      :open="viewsPanelOverlay"
      @dismiss="setViewsPanelCollapsed(true)"
    ></PanelOverlayScrim>
    <!-- The views rail runs the full height of the content column, so the
         header and the body below both start after it. It is hidden for public
         views, which expose a single shared view and have no list to switch. -->
    <ViewsPanel
      v-if="showViewsPanel"
      :database="database"
      :table="table"
      :views="views"
      :read-only="readOnly"
      :store-prefix="storePrefix"
      :collapsed="viewsPanelCollapsed"
      :overlay="viewsPanelOverlay"
      :toggle-actionable="viewsPanelToggleActionable"
      :forced-by-sidebar="viewsPanelForcedBySidebar && !viewsPanelNarrow"
      @update:collapsed="setViewsPanelCollapsed"
      @selected-view="$emit('selected-view', $event)"
    />
    <header
      ref="header"
      class="layout__col-2-1 header"
      :class="[
        { 'header--overflow': headerOverflow },
        openTriggerClass,
        contentOffsetClass,
        getViewHeaderClassNames(view),
      ]"
    >
      <div v-show="tableLoading" class="header__loading"></div>
      <!-- `Table.vue`'s own toolbar and the view-specific one rendered by
           `getViewHeaderComponent` below are two separate `<ul>`s from two
           separate components, but below `sm` they need to act as one
           continuous scrollable row -- the second `<ul>` holds Hide Fields,
           Row Height and Search, and without a shared scroll container the
           first `<ul>`'s own `overflow-x: auto` claimed the full row width
           for itself, squeezing the second to 0 and taking Search off
           screen with it. `header__filter-row` is that shared container;
           `header__filter` and `header__filter--full-width` no longer
           scroll themselves below `sm`, this wrapper does. -->
      <div v-if="!tableLoading" class="header__filter-row">
        <ul class="header__filter">
          <li v-if="showLogo" class="header__filter-item">
            <ExternalLinkBaserowLogo class="header__filter-logo" />
          </li>
          <!-- With the views rail present the list is always on screen, so the
             header only names the current view instead of duplicating the
             switcher. Public views keep the dropdown: they have no rail. -->
          <li
            v-if="showViewsPanel"
            class="header__filter-item header__filter-item--grids"
          >
            <div v-if="hasSelectedView" class="header__filter-link active">
              <i
                class="header__filter-icon header-filter-icon--view"
                :class="`${getViewColorClass(view)} ${getViewIconClass(view)}`"
              ></i>
              <span class="header__filter-name header__filter-name--forced">
                <EditableViewName ref="rename" :view="view"></EditableViewName>
              </span>
            </div>
          </li>
          <li v-else class="header__filter-item header__filter-item--grids">
            <a
              ref="viewsSelectToggle"
              class="header__filter-link active"
              :class="{ 'header__filter-link--disabled': views === null }"
              data-highlight="views"
              @click="views !== null && openTableViewsContext()"
            >
              <template v-if="hasSelectedView">
                <i
                  class="header__filter-icon header-filter-icon--view"
                  :class="`${getViewColorClass(view)} ${getViewIconClass(view)}`"
                ></i>
                <span class="header__filter-name header__filter-name--forced">
                  <EditableViewName
                    ref="rename"
                    :view="view"
                  ></EditableViewName>
                </span>
                <i
                  v-if="views !== null"
                  class="header__sub-icon iconoir-nav-arrow-down"
                ></i>
              </template>
              <template v-else-if="view !== null">
                {{ $t('table.chooseView') }}
                <i class="header__sub-icon iconoir-nav-arrow-down"></i>
              </template>
            </a>
            <ViewsContext
              v-if="views !== null"
              ref="viewsContext"
              :database="database"
              :table="table"
              :views="views"
              :read-only="readOnly"
              :header-overflow="headerOverflow"
              :store-prefix="storePrefix"
              @selected-view="$emit('selected-view', $event)"
            ></ViewsContext>
          </li>
          <li
            v-if="hasSelectedView && !readOnly && showViewContext"
            class="header__filter-item"
          >
            <a
              ref="viewSelectToggle"
              class="header__filter-link"
              data-highlight="view-options"
              @click="openTableViewContext"
            >
              <i class="header__filter-icon baserow-icon-more-vertical"></i>
            </a>
            <ViewContext
              ref="viewContext"
              :database="database"
              :view="view"
              :table="table"
              :views="views"
              :store-prefix="storePrefix"
              @enable-rename="$refs.rename.edit()"
            >
            </ViewContext>
          </li>
          <PublicViewMenu
            v-if="isPublic && hasSelectedView"
            :database="database"
            :table="table"
            :view="view"
            :fields="fields"
            :store-prefix="storePrefix"
          ></PublicViewMenu>
          <component
            :is="component"
            v-for="(component, index) in getAdditionalTableHeaderComponents(
              view,
              isPublic
            )"
            :key="index"
            :database="database"
            :table="table"
            :view="view"
            :fields="fields"
            :is-public-view="isPublic"
            :store-prefix="storePrefix"
          >
          </component>
          <li
            v-if="
              hasSelectedView &&
              view._.type.canFilter &&
              (adhocFiltering ||
                $hasPermission(
                  'database.table.view.create_filter',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
            data-highlight="view-filters"
          >
            <ViewFilter
              :view="view"
              :is-public-view="isPublic"
              :fields="fields"
              :database="database"
              :read-only="adhocFiltering"
              :disable-filter="disableFilter"
              @changed="refresh()"
            ></ViewFilter>
          </li>
          <li
            v-if="
              hasSelectedView &&
              view._.type.canSort &&
              (adhocSorting ||
                $hasPermission(
                  'database.table.view.create_sort',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
            data-highlight="view-sorts"
          >
            <ViewSort
              :database="database"
              :view="view"
              :fields="fields"
              :read-only="adhocSorting"
              :disable-sort="disableSort"
              :store-prefix="storePrefix"
              @changed="refresh($event)"
            ></ViewSort>
          </li>
          <li
            v-if="
              hasSelectedView &&
              view._.type.canGroupBy &&
              (adhocGrouping ||
                $hasPermission(
                  'database.table.view.create_group_by',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
            data-highlight="view-group-by"
          >
            <ViewGroupBy
              :database="database"
              :view="view"
              :fields="fields"
              :read-only="adhocGrouping"
              :disable-group-by="disableGroupBy"
              :store-prefix="storePrefix"
              @changed="refresh()"
            ></ViewGroupBy>
          </li>
          <li
            v-if="
              hasSelectedView &&
              view._.type.canShare &&
              !readOnly &&
              $hasPermission(
                'database.table.view.update_slug',
                view,
                database.workspace.id
              )
            "
            class="header__filter-item"
          >
            <ShareViewLink
              :view="view"
              :read-only="readOnly"
              :fields="fields"
              :views="views"
              :store-prefix="storePrefix"
            ></ShareViewLink>
          </li>
          <li
            v-if="
              hasSelectedView &&
              hasCompatibleDecorator &&
              (adhocDecorations ||
                $hasPermission(
                  'database.table.view.create_decoration',
                  view,
                  database.workspace.id
                ))
            "
            class="header__filter-item"
          >
            <ViewDecoratorMenu
              :database="database"
              :view="view"
              :table="table"
              :fields="fields"
              :read-only="adhocDecorations"
              :disable-sort="disableSort"
              :store-prefix="storePrefix"
              @changed="refresh()"
            ></ViewDecoratorMenu>
          </li>
        </ul>
        <component
          :is="getViewHeaderComponent(view)"
          v-if="hasSelectedView"
          :database="database"
          :table="table"
          :view="view"
          :fields="fields"
          :read-only="readOnly"
          :store-prefix="storePrefix"
          @refresh="refresh"
        />
      </div>
    </header>
    <div class="layout__col-2-2 content" :class="contentOffsetClass">
      <DefaultErrorPage v-if="viewError" :error="viewError" />
      <component
        :is="getViewComponent(view)"
        v-if="hasSelectedView && !tableLoading && !viewError"
        ref="view"
        :database="database"
        :table="table"
        :view="view"
        :loading="viewLoading"
        :fields="fields"
        :read-only="readOnly"
        :store-prefix="storePrefix"
        @refresh="refresh"
        @selected-row="$emit('selected-row', $event)"
        @navigate-previous="
          (row, activeSearchTerm) =>
            $emit('navigate-previous', row, activeSearchTerm)
        "
        @navigate-next="
          (row, activeSearchTerm) =>
            $emit('navigate-next', row, activeSearchTerm)
        "
      />
      <div v-if="viewLoading" class="loading-overlay"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { RefreshCancelledError } from '@baserow/modules/core/errors'
import { notifyIf } from '@baserow/modules/core/utils/error'
import PanelOverlayScrim from '@baserow/modules/core/components/sidebar/PanelOverlayScrim.vue'
import ViewsContext from '@baserow/modules/database/components/view/ViewsContext'
import ViewsPanel from '@baserow/modules/database/components/view/ViewsPanel'
import ViewContext from '@baserow/modules/database/components/view/ViewContext'
import PublicViewMenu from '@baserow/modules/database/components/view/PublicViewMenu'
import ViewFilter from '@baserow/modules/database/components/view/ViewFilter'
import ViewSort from '@baserow/modules/database/components/view/ViewSort'
import ViewDecoratorMenu from '@baserow/modules/database/components/view/ViewDecoratorMenu'
import ViewSearch from '@baserow/modules/database/components/view/ViewSearch'
import EditableViewName from '@baserow/modules/database/components/view/EditableViewName'
import ShareViewLink from '@baserow/modules/database/components/view/ShareViewLink'
import ExternalLinkBaserowLogo from '@baserow/modules/core/components/ExternalLinkBaserowLogo'
import ViewGroupBy from '@baserow/modules/database/components/view/ViewGroupBy'
import DefaultErrorPage from '@baserow/modules/core/components/DefaultErrorPage'
import { TaskQueue } from '@baserow/modules/core/utils/queue'
import {
  BREAKPOINT_MD,
  BREAKPOINT_SM,
  below,
} from '@baserow/modules/core/utils/breakpoints'
import {
  LEFT_NAV_SIDEBAR,
  LEFT_NAV_VIEWS,
} from '@baserow/modules/core/store/shell'
import {
  BAND_WIDE,
  PANEL_HIDDEN,
  PANEL_OVERLAY,
  panelIsIconOnly,
  panelToggleIsActionable,
} from '@baserow/modules/core/utils/shellPanelState'
import {
  getViewIconClass,
  getViewColorClass,
} from '@baserow/modules/database/utils/viewIdentity'

// The rail's width, the offset it imposes on the header and body, and the
// layout change below the breakpoint are all expressed in CSS now
// (`views_panel.scss`, `layout.scss`, `$breakpoint-md` in `_tokens.scss`).
// This component only says *whether the user collapsed the rail*; where that
// state turns into pixels is not its business.

// The one thing left that CSS cannot express is whether Escape should dismiss
// the rail, which is true exactly when it is floating over the grid. That uses
// the shared `BREAKPOINT_MD` and the shared `below()` helper, so the query here
// and the `below($breakpoint-md)` in `layout.scss` cannot drift apart.
const VIEWS_PANEL_STORAGE_KEY = 'baserow.viewsPanelCollapsed'

/**
 * The rail state is a personal UI preference rather than document state, so it
 * lives in localStorage and is scoped to the user, not to a table: making it
 * per-table would open and close the rail as you move between tables.
 */
function readViewsPanelCollapsed() {
  try {
    // Default to collapsed: the rail should never take width from the grid
    // until the user asks for it.
    return window.localStorage.getItem(VIEWS_PANEL_STORAGE_KEY) !== 'false'
  } catch (e) {
    // Storage can be unavailable (private mode, blocked cookies); the rail
    // must still work, just without remembering.
    return true
  }
}

function writeViewsPanelCollapsed(collapsed) {
  try {
    window.localStorage.setItem(VIEWS_PANEL_STORAGE_KEY, String(collapsed))
  } catch (e) {}
}

/**
 * This page component is the skeleton for a table. Depending on the selected view it
 * will load the correct components into the header and body.
 */
export default {
  name: 'Table',
  components: {
    DefaultErrorPage,
    ViewGroupBy,
    ExternalLinkBaserowLogo,
    ShareViewLink,
    EditableViewName,
    PanelOverlayScrim,
    ViewsContext,
    ViewsPanel,
    ViewDecoratorMenu,
    ViewFilter,
    ViewSort,
    ViewSearch,
    ViewContext,
    PublicViewMenu,
  },
  /**
   * Because there is no hook that is called before the route changes, we need the
   * tableLoading middleware to change the table loading state. This change will get
   * rendered right away. This allows us to have a custom loading animation when
   * switching views.
   */
  middleware: ['tableLoading'],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    views: {
      required: false,
      validator: (prop) => typeof prop === 'object' || prop === undefined,
      default: null,
    },
    view: {
      required: true,
      validator: (prop) => typeof prop === 'object' || prop === undefined,
    },
    viewError: {
      required: false,
      validator: (prop) =>
        typeof prop === 'object' ||
        typeof prop === 'function' ||
        prop === undefined,
      default: null,
    },
    tableLoading: {
      type: Boolean,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableSort: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableGroupBy: {
      type: Boolean,
      required: false,
      default: false,
    },
    storePrefix: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: [
    'navigate-next',
    'navigate-previous',
    'selected-row',
    'selected-view',
  ],
  data() {
    return {
      // Shows a small spinning loading animation when the view is being refreshed.
      viewLoading: false,
      // Indicates if the elements within the header are overflowing. In case of true,
      // we can hide certain values to make sure that it fits within the header.
      headerOverflow: false,
      // What the user chose for the views rail, independent of the viewport
      // and of the left navigation budget. Defaults to collapsed so the rail
      // never takes width from the grid before the user asks for it, and is
      // remembered per user (not per table) so switching tables doesn't make
      // the rail appear and disappear.
      //
      // Split from the effective `viewsPanelCollapsed` below for the same
      // reason `app.vue` splits `col1CollapsedPreference` from `col1Collapsed`:
      // something else can force the rail closed, and a force must never
      // overwrite what the user asked for.
      viewsPanelCollapsedPreference: true,
      // True while the viewport is below `$breakpoint-md`. The *layout* that
      // follows from this is CSS; this flag survives only because the rail
      // needs to know whether Escape should dismiss it, which is a behaviour,
      // not a layout.

      // The MediaQueryList backing the flag above. Held so it can be
      // unsubscribed on teardown.
      viewsPanelNarrowQuery: null,
      // Serializes concurrent refresh calls so each runs to completion before
      // the next starts. Sets viewLoading to false when the queue drains.
      refreshQueue: new TaskQueue({
        doneCallback: () => {
          this.$nextTick(() => {
            this.viewLoading = false
          })
        },
        onSuperseded: () => {
          try {
            this.$store.dispatch(this.storePrefix + 'view/grid/abortRefresh')
          } catch {
            // Non-grid view types don't have abortRefresh.
          }
        },
      }),
    }
  },
  computed: {
    /**
     * The views rail only makes sense where there is a list of views to switch
     * between. A public view exposes exactly one shared view, so it keeps the
     * plain header instead.
     */
    showViewsPanel() {
      return !this.isPublic && this.views !== null
    },
    /**
     * True while the sidebar holds the left navigation budget, so the rail may
     * not expand (Model C, `10-placement-proposal.md` §4.3).
     *
     * A force, not a preference. It is deliberately not folded into
     * `viewsPanelCollapsedPreference`, so that when the sidebar gives the
     * budget up the rail comes back exactly as the user left it.
     */
    viewsPanelForcedBySidebar() {
      return this.$store.getters['shell/isLeftNavForcedCollapsed'](
        LEFT_NAV_VIEWS
      )
    },
    /**
     * Whether the rail actually renders collapsed. Three independent reasons,
     * any one sufficient, none of them written back to storage except the
     * user's own: the viewport band, the left navigation budget, and the
     * preference.
     *
     * Inside the narrow band `viewsPanelNarrow` already forces it, so Model C
     * changes nothing there -- it is a no-op below `$breakpoint-md` rather than
     * a rule competing with the responsive one.
     */
    /**
     * Resolved by `store/shell.js`, not combined here.
     *
     * This used to be `narrow || forced || preference`, computed again in
     * `app.vue` for the sidebar, with no channel between the two. Worse, it sat
     * next to `viewsPanelOverlay = narrow && !collapsed`: substitute this
     * definition and `overlay` is false for every input, so a state that
     * `05-sidebar-system.md` §3.3 specifies, `views_panel.scss` styles, and this
     * component reads was unreachable from the day it was written.
     * `docs/hypedrive/17-shell-rearchitecture.md` §1.3.
     */
    viewsPanelState() {
      return this.$store.getters['shell/panelState'](LEFT_NAV_VIEWS)
    },
    /**
     * Derived from the band the store resolves, rather than a second
     * `matchMedia` flag tracking the same boundary. Still read for the things
     * that are not the panel state: whether Model C applies, and whether a
     * collapse is worth persisting.
     */
    viewsPanelNarrow() {
      return this.$store.getters['shell/viewportBand'] !== BAND_WIDE
    },
    // See `panelIsIconOnly` for why this is not simply "not expanded": that
    // definition used to also catch `overlay`, which measured live at 52px,
    // icons only -- the one state meant to show every view on a phone.
    viewsPanelCollapsed() {
      return panelIsIconOnly(this.viewsPanelState)
    },
    /**
     * True while the rail is floating over the grid rather than owning a
     * column. `views_panel.scss` styles that state from its own media query;
     * this is passed down only so the rail knows Escape should dismiss it,
     * which no stylesheet can do.
     */
    viewsPanelOverlay() {
      return this.viewsPanelState === PANEL_OVERLAY
    },
    /**
     * True below `sm` while the user has not opened the overlay. `views_panel.scss`
     * hides the panel entirely from this state -- there is no column and no
     * floating surface, so it draws nothing.
     */
    viewsPanelHidden() {
      return this.viewsPanelState === PANEL_HIDDEN
    },
    /**
     * How much room the header has to keep clear for the floating reopen
     * triggers, as a class rather than a number.
     *
     * The reservation used to be unconditional, on the reasoning that it "costs
     * nothing when only one or neither is showing". On a phone it costs the
     * whole width of the controls it is holding space for: 112px of a 390px
     * viewport, 29% of the screen, and the common case is neither trigger
     * showing -- a trigger only exists while its panel is hidden, and opening
     * either one takes its trigger away without giving the space back.
     */
    openTriggerClass() {
      const triggers =
        (this.sidebarHidden ? 1 : 0) +
        (this.showViewsPanel && this.viewsPanelHidden ? 1 : 0)
      return `header--open-triggers-${triggers}`
    },
    /**
     * Mirrors `col1IsHidden` in `app.vue`, which owns the sidebar but cannot
     * reach this header. Both read the same resolver, so they cannot disagree.
     */
    sidebarHidden() {
      return (
        this.$store.getters['shell/panelState'](LEFT_NAV_SIDEBAR) ===
        PANEL_HIDDEN
      )
    },
    /**
     * False in `[sm, md)`, where the viewport forces the rail and the toggle
     * cannot act -- the same reasoning as `app.vue`'s `col1ToggleActionable`.
     * Measured live before this existed: the toggle rendered, took focus, and a
     * click did nothing, in this component the sidebar's own toggle was fixed
     * for back in Phase 3c.
     */
    viewsPanelToggleActionable() {
      return panelToggleIsActionable(
        this.viewsPanelState,
        this.$store.getters['shell/viewportBand']
      )
    },
    /**
     * Header and body start after the rail. Which offset that is -- rail width,
     * collapsed width, or none because the rail is floating over them -- is a
     * function of the rail's collapsed state and the viewport width, and the
     * viewport half of that is a media query in `layout.scss`. So this only
     * reports the collapsed state as a class and lets CSS resolve the rest.
     *
     * It has to be a class rather than the inline `left` it used to be: an
     * inline style wins over any stylesheet rule, so a media query could never
     * have taken the offset back at narrow widths.
     */
    contentOffsetClass() {
      // Overlay is `position: fixed`, floating over the grid rather than
      // sharing a column with it -- the compact band's counterpart to the
      // `below($breakpoint-md)` query in `layout.scss` that reclaims the
      // offset for the medium band's floating rail, except there is no CSS
      // equivalent to reach for here: entering Overlay is a JS decision, not
      // a media query, so this has to say so itself. Hidden draws no column
      // either -- `views_panel.scss` sets `display: none` -- so it gets the
      // same treatment, or the grid would carry a 240px offset for a panel
      // that occupies no space at all.
      if (
        !this.showViewsPanel ||
        this.viewsPanelOverlay ||
        this.viewsPanelHidden
      ) {
        return null
      }
      return this.viewsPanelCollapsed
        ? 'layout__col-2--rail-collapsed'
        : 'layout__col-2--rail'
    },
    /**
     * Indicates if there is a selected view by checking if the view object has been
     * populated.
     */
    hasSelectedView() {
      return (
        this.view !== undefined &&
        Object.prototype.hasOwnProperty.call(this.view, '_')
      )
    },
    hasCompatibleDecorator() {
      return (
        this.view !== undefined &&
        this.$registry
          .getOrderedList('viewDecorator')
          .some((deco) => deco.isCompatible(this.view))
      )
    },
    showLogo() {
      return this.view?.show_logo && this.isPublic
    },
    showViewContext() {
      return (
        this.$hasPermission(
          'database.table.run_export',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.import_rows',
          this.table,
          this.database.workspace.id
        ) ||
        this.someViewHasPermission(
          'database.table.view.duplicate',
          this.table,
          this.database.workspace.id
        ) ||
        this.someViewHasPermission(
          'database.table.view.update',
          this.table,
          this.database.workspace.id
        ) ||
        this.someViewHasPermission(
          'database.table.view.delete',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.create_webhook',
          this.table,
          this.database.workspace.id
        )
      )
    },
    adhocFiltering() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_filter',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_filter',
          this.view,
          this.database.workspace.id
        )
      )
    },
    adhocSorting() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_sort',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_sort',
          this.view,
          this.database.workspace.id
        )
      )
    },
    adhocGrouping() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_group_bys',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_group_by',
          this.view,
          this.database.workspace.id
        )
      )
    },
    adhocDecorations() {
      if (this.readOnly) {
        return true
      }

      return (
        this.$hasPermission(
          'database.table.view.list_decoration',
          this.view,
          this.database.workspace.id
        ) &&
        !this.$hasPermission(
          'database.table.view.create_decoration',
          this.view,
          this.database.workspace.id
        )
      )
    },
    ...mapGetters({
      isPublic: 'page/view/public/getIsPublic',
    }),
  },
  watch: {
    tableLoading(value) {
      if (!value) {
        this.$nextTick(() => {
          this.checkHeaderOverflow()
        })
      }
    },
  },
  beforeMount() {
    this.$bus.$on('table-refresh', this.refresh)
    this.$bus.$on('open-table-views-context', this.openTableViewsContext)
    this.$bus.$on('close-table-views-context', this.closeTableViewsContext)

    // Restores which of the two left surfaces last held the budget. Normally a
    // no-op, because `app.vue` settles it during its own setup; this runs so
    // the rail is still correct if it ever does not. The fallback is only
    // consulted on a browser that has never stored a holder, and the rail
    // offers itself only when its own preference is already expanded.
    //
    // Before the first render rather than in `mounted`, because
    // `viewsPanelCollapsed` is read while rendering: hydrating a frame later
    // would paint the rail expanded and then snap it shut.
    this.$store.dispatch(
      'shell/hydrateLeftNav',
      readViewsPanelCollapsed() ? null : LEFT_NAV_VIEWS
    )
  },
  mounted() {
    // Observe the header itself, not the page root. The header's width is now
    // set by CSS -- its offset comes from a class the media query can revoke,
    // and the rail either takes a column or floats -- so the root can stay
    // exactly the same width while the header's own width changes underneath
    // it. Watching the root would miss precisely the resizes this measurement
    // exists for. The header is inside the root, so every resize that used to
    // fire still does.
    this.$el.resizeObserver = new ResizeObserver(this.checkHeaderOverflow)
    this.$el.resizeObserver.observe(this.$refs.header)

    // The rail's *layout* below the breakpoint is CSS (`views_panel.scss`,
    // `layout.scss`). This listener exists only for the two things CSS cannot
    // do: seed the collapsed state on entering the narrow band, and tell the
    // rail that Escape should now dismiss it. `matchMedia` fires only when the
    // band is crossed, unlike the `resize` handler it replaces, which ran on
    // every pixel of every drag.
    this.viewsPanelNarrowQuery = window.matchMedia(below(BREAKPOINT_MD))
    this.onViewsPanelNarrowChange(this.viewsPanelNarrowQuery)
    this.viewsPanelNarrowQuery.addEventListener(
      'change',
      this.onViewsPanelNarrowChange
    )
  },
  beforeUnmount() {
    this.$bus.$off('table-refresh', this.refresh)
    // `disconnect` rather than `unobserve(el)`: the element observed is the
    // header, and matching the argument to whatever was passed in `mounted` is
    // a thing to get wrong later. Disconnecting drops every observation
    // regardless, which is what teardown wants.
    this.$el?.resizeObserver?.disconnect()
    this.$bus.$off('open-table-views-context', this.openTableViewsContext)
    this.$bus.$off('close-table-views-context', this.closeTableViewsContext)
    this.viewsPanelNarrowQuery?.removeEventListener(
      'change',
      this.onViewsPanelNarrowChange
    )
  },
  methods: {
    // Exposed to the template because this is an Options API component: an
    // imported function is not in the render scope unless it is a method. The
    // header resolves a view's icon and colour through the same module the
    // rail uses (`utils/viewIdentity`), so a custom icon cannot show in one
    // surface and not the other -- which is exactly what happened while the
    // header read `view._.type.iconClass` directly.
    getViewIconClass,
    getViewColorClass,
    setViewsPanelCollapsed(collapsed) {
      // Below `sm` the rail has no column to collapse into: it is either hidden
      // or floating over the content. The toggle there opens and closes the
      // overlay, which is the same gesture meaning the same thing at a size
      // where a rail does not fit.
      if (this.viewsPanelState === PANEL_HIDDEN || this.viewsPanelOverlay) {
        this.$store.dispatch('shell/setOverlayOpen', {
          panel: LEFT_NAV_VIEWS,
          open: collapsed === false,
        })
        return
      }
      this.viewsPanelCollapsedPreference = collapsed
      this.syncViewsPanelPreference()
      // Below the breakpoint the rail is a transient overlay, so opening it
      // there is not a preference worth remembering for wide screens.
      if (!this.viewsPanelNarrow) {
        writeViewsPanelCollapsed(collapsed)
        // Model C. Expanding claims the left navigation budget, which forces
        // the sidebar to a rail; collapsing only releases it, and does not
        // expand the sidebar in its place -- otherwise "both collapsed" (state
        // L3) could never be reached. Skipped inside the narrow band, where the
        // sidebar is already a rail for viewport reasons and the budget is not
        // what is deciding anything.
        this.$store.dispatch(
          collapsed ? 'shell/releaseLeftNav' : 'shell/claimLeftNav',
          LEFT_NAV_VIEWS
        )
      }
      // The header has less room once the rail takes a column, so its
      // overflow handling has to re-measure against the new width.
      this.$nextTick(() => {
        this.checkHeaderOverflow()
      })
    },
    /**
     * Fires once when the viewport crosses `$breakpoint-md`, not on every
     * resize tick.
     *
     * Entering the narrow band always closes the rail so it cannot sit open
     * over the grid; leaving it restores whatever the user last chose. The
     * stored preference deliberately does not apply inside the band -- below
     * the breakpoint the rail always starts closed and floats.
     *
     * Only the preference is touched. `viewsPanelNarrow` is itself one of the
     * terms of the effective `viewsPanelCollapsed`, so the band does not need
     * to -- and must not -- write the collapse through.
     *
     * Leaving the band also re-enters the budget, because restoring an
     * *expanded* preference is an expansion like any other, and exclusivity is
     * asserted at the moment of expanding. Without this the rail would carry an
     * expanded preference that nobody had claimed the budget for, and it would
     * spring open unbidden the moment the sidebar released it.
     *
     * The claim is conditional on the budget being free. If the sidebar holds
     * it, the preference is left untouched and the rail simply stays forced --
     * stealing the budget on a resize would collapse a sidebar the user is
     * looking at, which is a viewport change causing a navigation change, and
     * that is the surprise the model is supposed to avoid.
     */
    /**
     * Mirrors the local preference into `store/shell.js`, which is what
     * resolves it against the band and the budget. The local ref stays as the
     * write path -- storage is still this component's key -- but it is no
     * longer what anything renders from.
     */
    syncViewsPanelPreference() {
      this.$store.dispatch('shell/setCollapsedPreference', {
        panel: LEFT_NAV_VIEWS,
        collapsed: this.viewsPanelCollapsedPreference,
      })
    },
    onViewsPanelNarrowChange(event) {
      this.viewsPanelCollapsedPreference = event.matches
        ? true
        : readViewsPanelCollapsed()
      this.syncViewsPanelPreference()
      this.$store.dispatch('shell/setViewportWidth', window.innerWidth)

      if (
        !event.matches &&
        !this.viewsPanelCollapsedPreference &&
        this.$store.getters['shell/leftNavExpanded'] === null
      ) {
        this.$store.dispatch('shell/claimLeftNav', LEFT_NAV_VIEWS)
      }
    },
    getViewComponent(view) {
      const type = this.$registry.get('view', view.type)
      return type.getComponent()
    },
    getViewHeaderComponent(view) {
      const type = this.$registry.get('view', view.type)
      return type.getHeaderComponent()
    },
    getViewHeaderClassNames(view) {
      if (!this.hasSelectedView) {
        return ''
      }
      const type = this.$registry.get('view', view.type)
      return type.getHeaderClassNames(view)
    },
    getAdditionalTableHeaderComponents(view, isPublic) {
      const opts = Object.values(this.$registry.getAll('plugin'))
        .reduce((components, plugin) => {
          components = components.concat(
            plugin.getAdditionalTableHeaderComponents(view, isPublic)
          )
          return components
        }, [])
        .filter((component) => component !== null)
      return opts
    },
    /**
     * When the window resizes, we want to check if the content of the header is
     * overflowing. If that is the case, we want to make some space by removing some
     * content. We do this by copying the header content into a new HTMLElement and
     * check if the elements still fit within the header. We copy the html because we
     * want to measure the header in the full width state.
     */
    checkHeaderOverflow() {
      // Below `sm` the header scrolls sideways (`header.scss`), so every control
      // is reachable however little room there is and there is nothing for this
      // to fix. Hiding the labels there would take away the names of controls
      // the user can simply scroll to -- two mechanisms solving one problem, the
      // second one undoing the first.
      //
      // It also cannot measure honestly at that width. The clone below is
      // reparented to `document.body`, which drops every ancestor selector --
      // including `header--open-triggers-N`, the class that reserves the
      // header's left padding for the floating triggers. The clone therefore
      // measures a header that is up to 112px wider than the real one.
      if (window.innerWidth < BREAKPOINT_SM) {
        this.headerOverflow = false
        return
      }

      const header = this.$refs.header
      const width = header.getBoundingClientRect().width
      const wrapper = document.createElement('div')
      wrapper.innerHTML = header.outerHTML
      const el = wrapper.childNodes[0]
      el.style = `position: absolute; left: 0; top: 0; width: ${width}px; overflow: auto;`
      el.classList.remove('header--overflow')
      document.body.appendChild(el)
      this.headerOverflow =
        el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight
      document.body.removeChild(el)
    },
    /**
     * Refreshes the whole view. All data will be reloaded and it will visually look
     * the same as seeing the view for the first time.
     */
    async refresh(event) {
      if (
        typeof event === 'object' &&
        Object.prototype.hasOwnProperty.call(event, 'tableId') &&
        event.tableId !== this.table.id
      ) {
        return
      }

      const callCallback = async () => {
        if (event && Object.prototype.hasOwnProperty.call(event, 'callback')) {
          await event.callback()
        }
      }

      const includeFieldOptions =
        typeof event === 'object' ? event.includeFieldOptions : false

      const fieldsToRefresh =
        typeof event === 'object' && event.newField
          ? [...this.fields, event.newField]
          : this.fields

      this.viewLoading = true
      const type = this.$registry.get('view', this.view.type)

      const taskId = this.refreshQueue.add(async () => {
        try {
          await type.refresh(
            { store: this.$store, app: this },
            this.database,
            this.view,
            fieldsToRefresh,
            this.storePrefix,
            includeFieldOptions,
            event?.sourceEvent
          )
        } catch (error) {
          if (error instanceof RefreshCancelledError) {
            await callCallback()
            return
          }
          notifyIf(error)
        }
        if (typeof this.$refs.view?.refresh === 'function') {
          await this.$refs.view.refresh()
        }
        await callCallback()
      })

      await this.refreshQueue.waitFor(taskId)
    },
    someViewHasPermission(op) {
      return this.views.some((v) =>
        this.$hasPermission(op, v, this.database.workspace.id)
      )
    },
    /**
     * "Show me the views" used to mean opening the dropdown. With the rail it
     * means expanding the rail, so callers on the bus (the guided tour) still
     * reveal the view list rather than hitting a dropdown that isn't rendered.
     */
    openTableViewsContext() {
      if (this.showViewsPanel) {
        this.setViewsPanelCollapsed(false)
        return
      }
      this.$refs.viewsContext.toggle(
        this.$refs.viewsSelectToggle,
        'bottom',
        'left',
        4
      )
    },
    closeTableViewsContext() {
      if (this.showViewsPanel) {
        this.setViewsPanelCollapsed(true)
        return
      }
      this.$refs.viewsContext.hide()
    },
    openTableViewContext() {
      this.$refs.viewContext.toggle(
        this.$refs.viewSelectToggle,
        'bottom',
        'left',
        4
      )
    },
  },
}
</script>
